/**
 * National Student Clearinghouse (NSC) — mock service.
 *
 * The real NSC "StudentTracker for Verification" service requires a signed
 * business contract, FERPA-compliant integration, and either bulk file
 * transfer (SFTP) or the NSC Web Services SOAP API. It is NOT available for
 * self-serve API use, so the prototype cannot wire it directly.
 *
 * Contract this mock exposes MATCHES the shape the real service will return,
 * so the swap becomes a one-file change in a backend adapter:
 *
 *   checkEnrollment(input) -> Promise<NSCResult>
 *
 *   input:  { fullName, school, gradYear, dob?, ssnLast4? }
 *   result: {
 *     status: "verified" | "not-found" | "unavailable" | "expired",
 *     records?: [{ school, level: "F"|"H"|"L"|"W", begin, end, classLevel }],
 *     confidence?: number 0..1,
 *     reasonCode?: string,     // e.g. "STU_NOT_ENROLLED", "SCHOOL_NOT_REPORTING"
 *     checkedAt: string ISO
 *   }
 *
 * Rules mocked:
 *   - Priya S. (WSU)     -> verified, full-time, Sophomore
 *   - Ethan L. (SJU)     -> verified, full-time, Freshman
 *   - Marcus J. (PVAMU)  -> not-found (school reports on lag; needs manual)
 *   - Jamal R. (CSUDH)   -> unavailable (service outage; needs manual)
 *   - Sarah T. (Brown)   -> verified, full-time, Sophomore
 *   Anything else        -> "unavailable" so admin defaults to manual path.
 *
 * TODO(backend): swap this mock for a real backend adapter that:
 *   1. Holds NSC credentials (never in-browser).
 *   2. Serves POST /api/verifications/nsc/check { studentId }
 *      with per-student rate limits + response caching.
 *   3. Signs requests + verifies responses per NSC SOAP schema.
 */

const nowIso = () => new Date().toISOString();

// Deterministic mock — same input -> same result for demo reproducibility.
const RESULTS = {
  "priya s.": {
    status: "verified", confidence: 0.99, checkedAt: nowIso(),
    records: [{ school: "Washington State University", level: "F",
                begin: "2025-08-25", end: "2025-12-19", classLevel: "Sophomore" }],
  },
  "ethan l.": {
    status: "verified", confidence: 0.98, checkedAt: nowIso(),
    records: [{ school: "St. John's University — MN", level: "F",
                begin: "2025-08-30", end: "2025-12-15", classLevel: "Freshman" }],
  },
  "marcus j.": {
    status: "not-found", confidence: 0, reasonCode: "SCHOOL_NOT_REPORTING",
    reasonText: "PVAMU has not reported to NSC for the current term yet. Try again after their next reporting window or verify manually.",
    checkedAt: nowIso(), records: [],
  },
  "jamal r.": {
    status: "unavailable", confidence: 0, reasonCode: "SERVICE_TIMEOUT",
    reasonText: "NSC service did not respond within the SLA. Try again in 10 minutes or verify manually.",
    checkedAt: nowIso(), records: [],
  },
  "sarah t.": {
    status: "verified", confidence: 0.99, checkedAt: nowIso(),
    records: [{ school: "Brown University", level: "F",
                begin: "2025-09-03", end: "2025-12-20", classLevel: "Sophomore" }],
  },
};

export const checkEnrollment = async (input) => {
  // Simulate the ~400-1200ms round trip a real SOAP call would take.
  await new Promise((r) => setTimeout(r, 700 + Math.random() * 500));

  const key = String(input.fullName || "").trim().toLowerCase();
  if (RESULTS[key]) return RESULTS[key];

  return {
    status: "unavailable",
    confidence: 0,
    reasonCode: "NO_MATCH",
    reasonText: "No record found for this student. Falls back to manual review.",
    checkedAt: nowIso(),
    records: [],
  };
};

// UI-friendly label + status pill class.
export const NSC_STATUS_META = {
  "verified":     { label: "NSC verified",     tone: "grass",     desc: "Enrollment confirmed by National Student Clearinghouse." },
  "not-found":    { label: "NSC no match",     tone: "tangerine", desc: "Student not currently enrolled per NSC — requires manual review." },
  "unavailable":  { label: "NSC unavailable",  tone: "cobalt",    desc: "NSC service temporarily unreachable — proceed with manual review." },
  "expired":      { label: "NSC record expired", tone: "tangerine", desc: "Previous enrollment record expired — requires re-verification." },
  "not-attempted": { label: "Not yet checked", tone: "olive",     desc: "NSC has not been queried for this student." },
};
